import { AppLayoutBody } from "@/components/layouts/AppLayout/AppLayoutBody";
import { AppLayoutSkeleton } from "@/components/layouts/AppLayout/AppLayoutSkeleton";
import { CommonProvider } from "@/contexts/CommonContext";
import { api } from "@/lib/api/api";
import { useGetAuthenticatedUser } from "@gettactic/helpers/src/hooks/useGetAuthenticatedUser";
import {
  AppAuthenticatedProvider,
  useAuthenticated,
  useSettings
} from "@gettactic/hooks";
import { MantineProvider, useMantineTheme } from "@mantine/core";
import Head from "next/head";
import React, { ReactNode } from "react";
import { AppMainLayout } from "@webV2/layouts/AppMainLayout";
import { isDesignSystem2 } from "@webV2/helpers/isDesignSystem2";
import { SettingsProvider } from "@v2/providers/SettingsProvider";
import { TacticProvider } from "@v2/providers/TacticProvider/TacticProvider";
import { isHostnameOrganizationSubDomain } from "@gettactic/helpers/src/url/isHostnameOrganizationSubDomain";
import { getOrganizationNameFromHostname } from "@gettactic//helpers/src/url/getOrganizationNameFromHostname";
import { getOrganizationUrlFromSlug } from "@gettactic/helpers/src/url/getOrganizationUrlFromSlug";
import { isAppHostname } from "@gettactic/helpers/src/url/isAppHostname";
import { EmbeddedLoginScreen } from "@/components/embedded/EmbeddedLoginScreen";

type AppLayoutProps = {
  children: React.ReactNode;
  hideOfficeMenu?: boolean;
  hideLayout?: boolean;
  theme?: string;
  changeTheme?: () => void;
};

const AppLayoutInner: React.FC<AppLayoutProps> = ({
  children,
  hideOfficeMenu = false,
  hideLayout = false,
  changeTheme,
  theme
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const defaultTheme = useMantineTheme();
  const { designSystem, isOrganizationSettingsLoading } = useSettings();
  const isV2 = isDesignSystem2(authenticatedUser, designSystem);

  React.useEffect(() => {
    if (isV2 && !document.body.classList.contains("tactic-v2")) {
      document.body.classList.add("tactic-v2");
    } else if (!isV2 && document.body.classList.contains("tactic-v2")) {
      document.body.classList.remove("tactic-v2");
    }
  }, [isV2]);

  if (!authenticatedUser || isOrganizationSettingsLoading) {
    return <AppLayoutSkeleton />;
  }

  return (
    <MantineProvider inherit theme={defaultTheme}>
      <div className="isolate flex h-screen flex-col">
        <CommonProvider>
          <TacticProvider>
            {hideLayout ? (
              children
            ) : !isV2 ? (
              <AppLayoutBody
                hideOfficeMenu={hideOfficeMenu}
                theme={theme}
                changeTheme={changeTheme}
              >
                {children}
              </AppLayoutBody>
            ) : (
              <AppMainLayout>{children}</AppMainLayout>
            )}
          </TacticProvider>
        </CommonProvider>
      </div>
    </MantineProvider>
  );
};

const AppLayout: React.FC<AppLayoutProps> = (props) => {
  const isAppSubdomain = isAppHostname();
  if (isAppSubdomain) api.setEmbeddedSettings();

  const authenticatedUser = useGetAuthenticatedUser(
    api,
    isAppSubdomain,
    isAppSubdomain
      ? {
          allowRedirect: false
        }
      : undefined
  );
  const firstOrganizationSlug =
    authenticatedUser?.organization?.slug ||
    authenticatedUser?.organizations?.[0]?.slug ||
    "";

  const isSubDomain = isHostnameOrganizationSubDomain();

  React.useEffect(() => {
    const organizationName = getOrganizationNameFromHostname();
    if (firstOrganizationSlug && (!isSubDomain || !organizationName)) {
      location.href = getOrganizationUrlFromSlug(firstOrganizationSlug);
    }
  }, [isSubDomain, firstOrganizationSlug]);

  if (!isSubDomain) return null;

  if (isAppSubdomain && !authenticatedUser) {
    return <EmbeddedLoginScreen />;
  }
  return (
    <>
      <Head>
        <meta name="googlebot" content="noindex, nofollow, noarchive" />
      </Head>
      {authenticatedUser ? (
        <AppAuthenticatedProvider
          api={api}
          authenticatedUser={authenticatedUser}
        >
          <SettingsProvider>
            <AppLayoutInner {...props} />
          </SettingsProvider>
        </AppAuthenticatedProvider>
      ) : null}
    </>
  );
};

export const getLayout = (page: ReactNode, pageProps: any) => (
  <AppLayout {...pageProps}>{page}</AppLayout>
);

export default AppLayout;
